/**
 * The conversation list, and the guard in front of deleting one.
 *
 * Extracted from the sidebar because the desktop rail and the mobile drawer
 * show the same list, and two copies would be two places to fix the day the
 * delete confirmation changes.
 */

import { useState } from "react";
import { Trash2 } from "lucide-react";
import type { Conversation } from "../../shared/types.ts";
import { Button } from "./ui/button.tsx";
import { ScrollArea } from "./ui/scroll-area.tsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog.tsx";

interface ThreadNavProps {
  readonly conversations: readonly Conversation[];
  readonly activeId: string | null;
  readonly onSelect: (id: string) => void;
  readonly onDelete: (id: string) => void;
}

export function ThreadNav({
  conversations,
  activeId,
  onSelect,
  onDelete,
}: ThreadNavProps) {
  const [pendingDelete, setPendingDelete] = useState<Conversation | null>(null);

  return (
    <>
      <ScrollArea className="-mr-1 min-h-0 flex-1 pr-1">
        <nav className="flex flex-col gap-px" aria-label="Conversations">
          {conversations.length === 0 ? (
            <p className="px-2 py-1 text-sm text-muted-foreground">
              Nothing yet.
            </p>
          ) : (
            conversations.map((conversation) => (
              <ThreadRow
                key={conversation.id}
                conversation={conversation}
                active={conversation.id === activeId}
                onSelect={() => onSelect(conversation.id)}
                onRequestDelete={() => setPendingDelete(conversation)}
              />
            ))
          )}
        </nav>
      </ScrollArea>

      <DeleteDialog
        conversation={pendingDelete}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) onDelete(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </>
  );
}

interface ThreadRowProps {
  readonly conversation: Conversation;
  readonly active: boolean;
  readonly onSelect: () => void;
  readonly onRequestDelete: () => void;
}

function ThreadRow({
  conversation,
  active,
  onSelect,
  onRequestDelete,
}: ThreadRowProps) {
  return (
    <div
      data-active={active}
      className="group/thread flex items-center rounded-md transition-colors hover:bg-sidebar-accent data-[active=true]:bg-sidebar-accent"
    >
      <button
        type="button"
        onClick={onSelect}
        className="min-w-0 flex-1 truncate px-2 py-1.5 text-left text-sm text-muted-foreground transition-colors group-hover/thread:text-sidebar-foreground group-data-[active=true]/thread:text-sidebar-foreground"
      >
        {conversation.title}
      </button>

      {/*
        Hidden until the row is hovered or the button is tabbed to: a delete
        control on every row is a lot of noise for a rare, destructive act.

        A coarse pointer has no hover to reveal it with, so on touch it stays
        visible and grows to 36px — the alternative is a 24px control that
        cannot be reached at all on a phone. Keyed off `hover: none` rather than
        a width breakpoint, because what matters is the input device: a narrow
        desktop window still has a cursor and should still stay quiet.
      */}
      <Button
        variant="ghost"
        size="icon-xs"
        onClick={onRequestDelete}
        aria-label={`Delete ${conversation.title}`}
        className="mr-1 text-muted-foreground opacity-0 transition-opacity group-hover/thread:opacity-100 hover:text-destructive focus-visible:opacity-100 [@media(hover:none)]:size-9 [@media(hover:none)]:opacity-100"
      >
        <Trash2 />
      </Button>
    </div>
  );
}

interface DeleteDialogProps {
  readonly conversation: Conversation | null;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

function DeleteDialog({ conversation, onCancel, onConfirm }: DeleteDialogProps) {
  return (
    <AlertDialog
      open={conversation !== null}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <AlertDialogContent className="font-sans">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
          <AlertDialogDescription>
            “{conversation?.title}” and every answer in it will be removed. This
            cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
